/**
 * DetailPanel — die Komponenten-Details als Dialog.
 *
 * Aus dem festen Seitenpanel wird ein zentriertes Sheet: prince-ui `Modal`
 * (`primitives/overlays.tsx:43`) bringt über React Aria Fokusfalle, Esc und
 * Fokus-Rückgabe mit — nichts davon ist hier von Hand gebaut.
 *
 * Der Inhalt ist 1:1 der bisherige (Beschreibung, Pfad, Schlüsseldateien,
 * Technologien, Benötigt, Liefert an, Skill, Drill-Down, Hinweise) plus
 * sichtbarem Deep-Link.
 *
 * Eine bewusste Verhaltensänderung: der Kopf-Badge zeigt wieder den BAUzustand.
 *
 * **Ziele nur auf einer Seite.** `goals` schaltet Ziel-Chip, Zieldatum und den
 * abgeleiteten Grad zu — eingeschaltet ausschließlich von `/umsetzungsgrad`.
 * Auf `/architecture` bleibt der Dialog zielfrei; dort stört das Ziel.
 *
 * Geöffnet wird über die Route `<basePath>/:componentId`. `House` reicht die
 * gewählte Komponente und den `basePath` herein, `onClose` navigiert zurück.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Icon, Modal } from "@conuti-das/prince-ui";
import { princeIcon } from "@/ui-prince";
import { StateGlyph, stateWord } from "@/components/flow";
import { GoalBadge, findGoal } from "@/components/GoalBadge";
import { GradeCell, gradeForRef } from "@/components/goals";
import { buildState } from "./archModel";
import type { HouseComponent } from "./houseData";
import "./house.css";

const TITLE_ID = "arch-sheet-title";

export function DetailPanel({
  component,
  basePath = "/architecture",
  goals = false,
  onClose,
}: {
  component?: HouseComponent;
  basePath?: string;
  goals?: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={!!component}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-labelledby={TITLE_ID}
      className="arch-sheet"
    >
      {component && (
        <SheetBody component={component} basePath={basePath} goals={goals} onClose={onClose} />
      )}
    </Modal>
  );
}

function SheetBody({
  component,
  basePath,
  goals,
  onClose,
}: {
  component: HouseComponent;
  basePath: string;
  goals: boolean;
  onClose: () => void;
}) {
  const state = buildState(component);
  const goal = goals ? findGoal(`repo:${component.id}`) : undefined;

  return (
    <>
      <div className="arch-sheet-head">
        <span data-state={state} className="arch-sheet-glyph">
          <StateGlyph state={state} size={22} />
        </span>
        <div className="arch-sheet-ident">
          <h3 id={TITLE_ID} className="arch-sheet-title">
            {component.name}
          </h3>
          <span className="arch-sheet-type">
            {component.type} · {stateWord(state, "build")}
          </span>
          {goals && (
            <span className="arch-sheet-goal">
              {/* Ziel-Chip — Termin-Urteil über den Bauzustand, nicht sein Ersatz. */}
              <GoalBadge refId={`repo:${component.id}`} />
              {goal?.target_date && goal.status !== "done" && (
                <Badge tone="gray" variant="soft">{`Ziel: ${goal.target_date}`}</Badge>
              )}
              {/* Der abgeleitete Grad — getrennt beschriftet, damit er nicht
                  als Zielfortschritt aus Atlassian gelesen wird. */}
              <GradeCell grade={gradeForRef(`repo:${component.id}`)} />
            </span>
          )}
          <code className="arch-sheet-deep">{`${basePath}/${component.id}`}</code>
        </div>
        <button type="button" className="arch-sheet-close" aria-label="Schließen" onClick={onClose}>
          <span aria-hidden="true">✕</span>
        </button>
      </div>

      <div className="arch-sheet-body">
        <Section title="Beschreibung">
          <p className="arch-sheet-text">{component.description}</p>
        </Section>

        <Section title="Pfad">
          <code className="arch-sheet-path">{component.path}</code>
        </Section>

        {component.keyFiles && component.keyFiles.length > 0 && (
          <Section title="Schlüsseldateien">
            <ArrowList items={component.keyFiles} />
          </Section>
        )}

        {component.technologies && component.technologies.length > 0 && (
          <Section title="Technologien">
            <div className="arch-sheet-chips">
              {component.technologies.map((t) => (
                <Badge key={t} tone="teal" variant="soft">
                  {t}
                </Badge>
              ))}
            </div>
          </Section>
        )}

        {component.dependsOn && component.dependsOn.length > 0 && (
          <Section title="Benötigt">
            <ArrowList items={component.dependsOn} />
          </Section>
        )}

        {component.feedsInto && component.feedsInto.length > 0 && (
          <Section title="Liefert an">
            <ArrowList items={component.feedsInto} />
          </Section>
        )}

        {component.skill && (
          <Section title="Claude-Code-Skill">
            <code className="arch-sheet-path">{component.skill}</code>
          </Section>
        )}

        {component.drillDown && (
          <Link to={component.drillDown} className="arch-sheet-drill">
            <Button variant="filled">
              {`Drill-Down zu ${component.drillDown}`}
              <Icon name={princeIcon("navigation-right-arrow")} size={16} />
            </Button>
          </Link>
        )}

        {component.notes && (
          <Section title="Hinweise">
            <p className="arch-sheet-note">{component.notes}</p>
          </Section>
        )}
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="section-title">{title}</div>
      {children}
    </div>
  );
}

function ArrowList({ items }: { items: string[] }) {
  return (
    <ul className="arch-sheet-list">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}
